import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { GetCollectingRequestsParams, GetCollectingRequestsResponse } from '~/types/delivery-agency.types';

export const fetchCollectingRequests = async (
  params: GetCollectingRequestsParams
): Promise<GetCollectingRequestsResponse> => {
  const { data } = await axiosInstance.get<GetCollectingRequestsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUESTS,
    { params }
  );
  return data;
};

type UseGetCollectingRequestsQueryOptions = {
  params: GetCollectingRequestsParams | null;
  showGlobalDimOverlay?: boolean;
};

export const useGetCollectingRequestsQuery = ({
  params,
  showGlobalDimOverlay = true,
}: UseGetCollectingRequestsQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.collectingRequests(params),
    queryFn: params ? () => fetchCollectingRequests(params) : skipToken,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalDimOverlay },
  });
};

registerMockAPI<GetCollectingRequestsResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUESTS,
  label: '배송 주문 반품 신청·조회/목록',
  scenarios: [
    {
      name: '정상 응답 (전 상태 조합)',
      response: {
        data: [
          {
            collectingRequestedDate: '2026-08-04',
            collectingRequestStatus: { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
            statusUpdatedAt: null,
            collectingRequestCode: 'RR20260729001',
            collectingShippingLabelNumbers: ['', ''],
            productName: '유리컵 2개',
            collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
            contactName: '홍**',
            originalOutboundCode: 'OB20260728001',
            clientOrderCodes: ['ORD-20260728-0001'],
          },
          {
            collectingRequestedDate: '2026-08-04',
            collectingRequestStatus: { code: 'COLLECT_DEPARTURE_READY', description: '회수 출발 준비' },
            statusUpdatedAt: '2026-08-04 21:03:15',
            collectingRequestCode: 'RR20260729007',
            collectingShippingLabelNumbers: ['CL20260729007'],
            productName: '텀블러',
            collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
            contactName: '최**',
            originalOutboundCode: 'OB20260728007',
            clientOrderCodes: ['ORD-20260728-0007'],
          },
          {
            collectingRequestedDate: '2026-08-04',
            collectingRequestStatus: { code: 'COLLECT_ON_THE_WAY', description: '회수 중' },
            statusUpdatedAt: '2026-08-04 15:21:07',
            collectingRequestCode: 'RR20260729002',
            collectingShippingLabelNumbers: ['CL20260729002', 'CL20260729003'],
            productName: '전기포트',
            collectedProductHandling: { code: 'RETURN_TO_SENDER', description: '다시 돌려받기(반송)' },
            contactName: '김**',
            originalOutboundCode: 'OB20260728002',
            clientOrderCodes: ['ORD-20260728-0002', 'ORD-20260728-0003'],
          },
          {
            collectingRequestedDate: '2026-08-03',
            collectingRequestStatus: { code: 'COLLECT_PARTIAL', description: '회수 완료(부분)' },
            statusUpdatedAt: '2026-08-03 18:44:02',
            collectingRequestCode: 'RR20260728004',
            collectingShippingLabelNumbers: ['CL20260728004', 'CL20260728005', 'CL20260728006'],
            productName: '접시 세트',
            collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
            contactName: '이**',
            originalOutboundCode: 'OB20260727004',
            clientOrderCodes: ['ORD-20260727-0004'],
          },
          {
            collectingRequestedDate: '2026-08-03',
            collectingRequestStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
            statusUpdatedAt: '2026-08-03 17:55:40',
            collectingRequestCode: 'RR20260728005',
            collectingShippingLabelNumbers: ['CL20260728007'],
            productName: '텀블러',
            collectedProductHandling: { code: 'RETURN_TO_SENDER', description: '다시 돌려받기(반송)' },
            contactName: '박**',
            originalOutboundCode: 'OB20260727005',
            clientOrderCodes: [],
          },
          {
            collectingRequestedDate: '2026-08-02',
            collectingRequestStatus: { code: 'COLLECT_FAILED', description: '회수 실패' },
            statusUpdatedAt: '2026-08-02 16:30:11',
            collectingRequestCode: 'RR20260727006',
            collectingShippingLabelNumbers: ['CL20260727008'],
            productName: '무선 이어폰',
            collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
            contactName: '최**',
            originalOutboundCode: 'OB20260726006',
            clientOrderCodes: ['ORD-20260726-0006'],
          },
          {
            collectingRequestedDate: '2026-08-02',
            collectingRequestStatus: { code: 'COLLECT_CANCELED', description: '회수 취소' },
            statusUpdatedAt: '2026-08-02 11:10:55',
            collectingRequestCode: 'RR20260727007',
            collectingShippingLabelNumbers: [],
            productName: '샐러드 볼',
            collectedProductHandling: { code: 'RETURN_TO_SENDER', description: '다시 돌려받기(반송)' },
            contactName: '정**',
            originalOutboundCode: 'OB20260726007',
            clientOrderCodes: ['ORD-20260726-0007', 'ORD-20260726-0008', 'ORD-20260726-0009'],
          },
        ],
        pagination: { cursor: 1, listView: 50, listTotal: 7 },
      },
    },
    {
      name: '데이터 없음',
      response: { data: [], pagination: { cursor: 1, listView: 50, listTotal: 0 } },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: { data: [], pagination: { cursor: 1, listView: 50, listTotal: 0 } },
    },
  ],
});
